import { createSelector } from 'reselect';

import {
  creatorFilterAllOption,
  pageNameFilterAllOption,
  shareOptions,
} from '../../domain/home/consistent';

/* ---------------------------------------------------------------
 * Action Types
 */

const SELECT_HOME_FIELDS_SHARE_FILTER =
  'SELECT_HOME_FIELDS_SHARE_FILTER' as const;
const SELECT_HOME_FIELDS_PAGE_NAME_FILTER =
  'SELECT_HOME_FIELDS_PAGE_NAME_FILTER' as const;
const SELECT_HOME_FIELDS_CREATOR_FILTER =
  'SELECT_HOME_FIELDS_CREATOR_FILTER' as const;

export const HomeSettingActionTypes = {
  SELECT_HOME_FIELDS_SHARE_FILTER,
  SELECT_HOME_FIELDS_PAGE_NAME_FILTER,
  SELECT_HOME_FIELDS_CREATOR_FILTER,
};

/* ---------------------------------------------------------------
 * Action Creators
 */

/**
 * 表示項目の共有のフィルターの変更
 */
function selectHomeFieldsShareFilterAction(
  shareOption: (typeof shareOptions)[number]
) {
  return {
    type: SELECT_HOME_FIELDS_SHARE_FILTER,
    payload: { shareOption },
  };
}

/**
 * 対象画面のフィルターの変更
 */
function selectHomeFieldsPageNameFilterAction(pageName: string) {
  return {
    type: SELECT_HOME_FIELDS_PAGE_NAME_FILTER,
    payload: { pageName },
  };
}

/**
 * 作成者のフィルターの変更
 */
function selectHomeFieldsCreatorFilterAction(creatorName: string) {
  return {
    type: SELECT_HOME_FIELDS_CREATOR_FILTER,
    payload: { creatorName },
  };
}

export const HomeSettingActionCreators = {
  selectHomeFieldsShareFilterAction,
  selectHomeFieldsPageNameFilterAction,
  selectHomeFieldsCreatorFilterAction,
};

/* ---------------------------------------------------------------
 * Actions
 */

type HomeSettingAction =
  | ReturnType<typeof selectHomeFieldsShareFilterAction>
  | ReturnType<typeof selectHomeFieldsPageNameFilterAction>
  | ReturnType<typeof selectHomeFieldsCreatorFilterAction>;

/* ---------------------------------------------------------------
 * State
 */

type HomeSettingState = {
  fields: {
    share: {
      filter: (typeof shareOptions)[number];
    };
    pageName: {
      filter: string;
    };
    creator: {
      filter: string;
    };
  };
};

const initialState: HomeSettingState = {
  fields: {
    share: {
      filter: 'すべて',
    },
    pageName: {
      filter: pageNameFilterAllOption,
    },
    creator: {
      filter: creatorFilterAllOption,
    },
  },
};

/* ---------------------------------------------------------------
 * Selector
 */

function homeSettingSelector(rootState: { homeSetting: HomeSettingState }) {
  return rootState.homeSetting;
}

/**
 * [ホーム] 表示項目の共有で利用されるフィルター
 */
export const homeSettingFieldsShareFilterSelector = createSelector(
  homeSettingSelector,
  (homeSetting) => {
    return homeSetting.fields.share.filter;
  }
);

/**
 * [ホーム] 対象画面で利用されるフィルター
 */
export const homeSettingFieldsPageNameFilterSelector = createSelector(
  homeSettingSelector,
  (homeSetting) => {
    return homeSetting.fields.pageName.filter;
  }
);

/**
 * [ホーム] 作成者で利用されるフィルター
 */
export const homeSettingFieldsCreatorFilterSelector = createSelector(
  homeSettingSelector,
  (homeSetting) => {
    return homeSetting.fields.creator.filter;
  }
);

/* ---------------------------------------------------------------
 * Reducer
 */

export function homeSettingReducer(
  state = initialState,
  action: HomeSettingAction
): HomeSettingState {
  switch (action.type) {
    case SELECT_HOME_FIELDS_SHARE_FILTER:
      return {
        ...state,
        fields: {
          ...state.fields,
          share: {
            ...state.fields.share,
            filter: action.payload.shareOption,
          },
        },
      };
    case SELECT_HOME_FIELDS_PAGE_NAME_FILTER:
      return {
        ...state,
        fields: {
          ...state.fields,
          pageName: {
            ...state.fields.pageName,
            filter: action.payload.pageName,
          },
        },
      };
    case SELECT_HOME_FIELDS_CREATOR_FILTER:
      return {
        ...state,
        fields: {
          ...state.fields,
          creator: {
            ...state.fields.creator,
            filter: action.payload.creatorName,
          },
        },
      };

    default:
      return state;
  }
}
